import type { MeetingResult } from "./engine";
import { MEETING_TEMPLATES } from "./engine";

export function buildEmailSummary(r: MeetingResult, projectName = ""): string {
  const title = projectName ? `${projectName} — ` : "";
  return `Subject: Meeting Summary — ${title}${r.date}

Hi Team,

Thank you for the productive ${MEETING_TEMPLATES[r.type].label.toLowerCase()} session. Here's a summary of what we covered:

KEY DECISIONS
${r.decisions.length ? r.decisions.map((d, i) => `${i + 1}. ${d}`).join("\n") : "(none captured)"}

ACTION ITEMS
${
  r.action_items.length
    ? r.action_items
        .map((a, i) => `${i + 1}. ${a.task} — Owner: ${a.owner} | Due: ${a.deadline} | Priority: ${a.priority}`)
        .join("\n")
    : "(none captured)"
}

OPEN QUESTIONS
${r.open_questions.length ? r.open_questions.map((q, i) => `${i + 1}. ${q}`).join("\n") : "(none captured)"}

SUGGESTED REQUIREMENTS
${
  r.suggested_requirements.length
    ? r.suggested_requirements.map((s, i) => `${i + 1}. ${s.text} [${s.priority}]`).join("\n")
    : "(none derived)"
}

Please review and confirm your action items. Reply if anything is missing.

Best,
[Your Name]`;
}

function csvEscape(v: string): string {
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

export function buildJiraCsv(r: MeetingResult): string {
  const header = ["Summary", "IssueType", "Assignee", "Priority", "Description"];
  const rows = r.action_items.map((a) => [
    csvEscape(a.task),
    "Task",
    csvEscape(a.owner),
    a.priority,
    csvEscape(`From meeting ${r.date}. Owner: ${a.owner}. Due: ${a.deadline}`),
  ]);
  return [header.join(","), ...rows.map((row) => row.join(","))].join("\n");
}

export function buildRequirementsMarkdown(r: MeetingResult): string {
  const lines: string[] = [`# Suggested Requirements — ${r.date}`, ""];
  if (!r.suggested_requirements.length) {
    lines.push("_No feature-related decisions detected in this meeting._");
    return lines.join("\n");
  }
  r.suggested_requirements.forEach((req, i) => {
    lines.push(`## REQ-${String(i + 1).padStart(3, "0")}`);
    lines.push(`**Priority:** ${req.priority}`);
    lines.push(`**Source:** ${req.source}`);
    lines.push("");
    lines.push(`**User Story:** ${req.text}`);
    lines.push("");
    lines.push("**Acceptance Criteria:**");
    lines.push("- [ ] TBD");
    lines.push("");
  });
  return lines.join("\n");
}

export function buildMinutesMarkdown(r: MeetingResult, projectName = ""): string {
  const lbl = MEETING_TEMPLATES[r.type].label;
  const lines: string[] = [
    `# ${lbl} Meeting Minutes`,
    projectName ? `**Project:** ${projectName}` : "",
    `**Date:** ${r.date}`,
    "",
    "## Key Decisions",
    ...(r.decisions.length ? r.decisions.map((d) => `- ${d}`) : ["- (none)"]),
    "",
    "## Action Items",
    "| # | Task | Owner | Deadline | Priority |",
    "|---|------|-------|----------|----------|",
    ...(r.action_items.length
      ? r.action_items.map(
          (a, i) => `| ${i + 1} | ${a.task} | ${a.owner} | ${a.deadline} | ${a.priority} |`,
        )
      : ["| - | (none) | - | - | - |"]),
    "",
    "## Open Questions",
    ...(r.open_questions.length ? r.open_questions.map((q) => `- ${q}`) : ["- (none)"]),
    "",
    "## Suggested Requirements",
    ...(r.suggested_requirements.length
      ? r.suggested_requirements.map((s) => `- ${s.text} _[${s.priority}]_`)
      : ["- (none)"]),
  ];
  return lines.filter((l) => l !== null).join("\n");
}

export function download(filename: string, content: string, mime = "text/plain") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
